import React, { useState } from 'react'
import { Alert, IconButton, Typography } from '@mui/material'
import { HelpOutline } from '@mui/icons-material'
import { com } from '@eidu/form'
import LabelLineDisplay from './LabelLineDisplay'
import FieldType = com.eidu.sharedlib.entity.field.FieldType
import Field = com.eidu.sharedlib.entity.field.Field
import EntityLabelLine = com.eidu.sharedlib.entity.label.EntityLabelLine

export type LabelDisplayProps = {
  fields: readonly Field[]
  primaryLabel: EntityLabelLine
  secondaryLabel: EntityLabelLine | undefined
}

const LabelDisplay = ({ fields, primaryLabel, secondaryLabel }: LabelDisplayProps) => {
  const [showInstructions, setShowInstructions] = useState(false)

  const availablePrimaryLabelFields: Field[] = fields.filter(
    (field) => field.type === FieldType.Text || field.type === FieldType.Number || field.type === FieldType.Date
  )

  const availableSecondaryLabelFields: Field[] = fields.filter(
    (field) =>
      field.type === FieldType.Text ||
      field.type === FieldType.Number ||
      field.type === FieldType.Date ||
      field.type === FieldType.Reference
  )

  return (
    <>
      <Typography variant="h6">
        Label
        <IconButton color="primary" onClick={() => setShowInstructions(!showInstructions)}>
          <HelpOutline sx={{ fontSize: 18 }} />
        </IconButton>
      </Typography>

      {showInstructions && (
        <Alert severity="info" onClose={() => setShowInstructions(false)}>
          <Typography>
            To create an entity label, use curly braces <code>{}</code> to include the values of specific fields in your
            text. The label consists of two lines:
            <ol>
              <li>
                <strong>First Line:</strong> You can reference fields of type <i>Text</i>, <i>Number</i>, or <i>Date</i>
                . Simply type the text you want, and where you need the value of a field, enclose the field name in
                curly braces. For example, <code>&laquo;{'Name: {First Name}'}&raquo;</code> will display
                <i>&laquo;Name: &raquo;</i> followed by the value in the <code>First Name</code> field.
              </li>
              <li>
                <strong>Second Line:</strong> In addition to <i>Text</i>, <i>Number</i>, and <i>Date</i> fields, you may
                also reference <i>Reference</i> type fields here. Write your text, and use curly braces to include
                fields as needed. For example, <code>&laquo;{'{Linked Entity}'}&raquo;</code> would display the
                reference field <code>Linked Entity</code>.
              </li>
            </ol>
          </Typography>

          <Typography>
            Ensure your field names match exactly, including capitalization, and enclose each field name within{' '}
            <code>{'{}'}</code> to ensure it renders correctly.
          </Typography>
        </Alert>
      )}
      {availablePrimaryLabelFields.length === 0 && (
        <Typography>
          <strong>
            <br />
            No suitable fields found.
          </strong>
        </Typography>
      )}

      <LabelLineDisplay
        label={primaryLabel}
        title="First Line (required)"
        availableFields={availablePrimaryLabelFields}
      />

      <LabelLineDisplay
        label={secondaryLabel}
        title="Second Line (optional)"
        availableFields={availableSecondaryLabelFields}
      />
    </>
  )
}

export default LabelDisplay
